import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  downloadAircraftDocument,
  getAircraft,
  getAircraftDocumentFile,
  getAircraftDocuments,
  getFinance,
  getHoursCounters,
  getRemainingTo100h,
  getRemainingTo50h,
} from '../../api';
import {
  Aircraft,
  AircraftDocument,
  AircraftDocumentType,
  RemainingTO,
} from '../../types';
import { aircraftLabel, useAircraft } from '../../aircraft/AircraftContext';
import { useAuth } from '../../hooks/useAuth';
import { useFeedback } from '../../hooks/useFeedback';
import { DateFormat } from '../../utils/DateFormat';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatMetric } from '../../utils/formatMetric';
import { formatHoursPart, remainingOverdue } from '../../utils/toRemaining';
import {
  blobWithMime,
  previewKindFromName,
  type PreviewKind,
} from '../../utils/filePreview';
import PageBackLink from '../../components/PageBackLink/PageBackLink';
import DocumentPreviewModal from '../../components/DocumentPreviewModal/DocumentPreviewModal';
import ModalPortal from '../../components/modal/ModalPortal';
import '../../components/modal/ModalBase.css';
import {
  checkLine,
  currentSharePeriod,
  emptyCheck,
  filesCountLabel,
  formatShareRange,
  formatSharesLine,
  maintenanceLabel,
  psFilesLine,
  visibleOwnershipShares,
} from './aircraftHub';
import './AircraftPage.css';

const pickNearestTo = (to50: RemainingTO | null, to100: RemainingTO | null) => {
  const active = [to50, to100].find((item) => item?.active);
  if (active) return active;
  return to50?.next_required === 'ТО-50' ? to50 : to100 || to50;
};

type HubCardProps = {
  to?: string;
  label: string;
  value: string;
  unit?: string;
  footer: string;
  warn?: boolean;
};

const HubCard = ({ to, label, value, unit, footer, warn }: HubCardProps) => {
  const inner = (
    <>
      <span className="aircraft-hub-label">{footer}</span>
      <span className={`aircraft-hub-value${warn ? ' warn' : ''}`}>
        <span className="aircraft-hub-number">
          {value}
          {value !== '—' && unit ? (
            <span className="aircraft-hub-unit">{unit}</span>
          ) : null}
        </span>
      </span>
      <span className="aircraft-hub-footer">{label}</span>
    </>
  );

  if (!to) {
    return <div className="aircraft-hub-card is-static">{inner}</div>;
  }

  return (
    <Link to={to} className="aircraft-hub-card">
      {inner}
    </Link>
  );
};

const AircraftPage = () => {
  const { aircraftId, aircraftList, refreshList, setAircraftId, dataRevision } =
    useAircraft();
  const { canMaintain, isGuest, user } = useAuth();
  const { showError, feedbackNode } = useFeedback();
  const dateFormat = user?.date_format === 'long' ? 'long' : 'dot';
  const [aircraft, setAircraft] = useState<Aircraft | null>(null);
  const [documents, setDocuments] = useState<AircraftDocument[]>([]);
  const [totalHours, setTotalHours] = useState<number | null>(null);
  const [to50, setTo50] = useState<RemainingTO | null>(null);
  const [to100, setTo100] = useState<RemainingTO | null>(null);
  const [hourCostAll, setHourCostAll] = useState<number | null>(null);
  const [sharesOpen, setSharesOpen] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<AircraftDocument[]>([]);
  const [previewDoc, setPreviewDoc] = useState<AircraftDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewKind, setPreviewKind] = useState<PreviewKind>('other');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewRequest = useRef(0);

  const load = useCallback(async () => {
    try {
      const [aircraftRes, docsRes] = await Promise.all([
        getAircraft(),
        getAircraftDocuments().catch(() => ({
          data: [] as AircraftDocument[],
        })),
      ]);
      setAircraft(aircraftRes.data);
      setDocuments(docsRes.data || []);
    } catch {
      setAircraft(null);
      setDocuments([]);
      setTotalHours(null);
      setTo50(null);
      setTo100(null);
      setHourCostAll(null);
      return;
    }
    try {
      const [hoursRes, to50Res, to100Res] = await Promise.all([
        getHoursCounters(),
        getRemainingTo50h(),
        getRemainingTo100h(),
      ]);
      setTotalHours(hoursRes.data.total_hours ?? null);
      setTo50(to50Res.data);
      setTo100(to100Res.data);
    } catch {
      setTotalHours(null);
      setTo50(null);
      setTo100(null);
    }
    try {
      if (isGuest) {
        setHourCostAll(null);
      } else {
        const financeRes = await getFinance({ params: { limit: 0 } });
        setHourCostAll(financeRes.data.hour_cost_all ?? null);
      }
    } catch {
      setHourCostAll(null);
    }
  }, [isGuest]);

  useEffect(() => {
    void load();
  }, [aircraftId, dataRevision, load]);

  const handleFleetChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    if (!Number.isFinite(id)) return;
    if (id !== aircraftId) setAircraftId(id);
  };

  const closePreview = () => {
    previewRequest.current += 1;
    setPreviewDoc(null);
    setPreviewFiles([]);
    setPreviewLoading(false);
    setPreviewError(null);
    setPreviewKind('other');
    setPreviewUrl(null);
  };

  const handlePreview = async (doc: AircraftDocument) => {
    const requestId = previewRequest.current + 1;
    previewRequest.current = requestId;
    const name = doc.original_name || doc.filename;
    const kind = previewKindFromName(name);
    setPreviewUrl(null);
    setPreviewDoc(doc);
    setPreviewKind(kind);
    setPreviewError(null);
    if (kind === 'other') {
      setPreviewLoading(false);
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await getAircraftDocumentFile(doc.id);
      if (previewRequest.current !== requestId) return;
      const blob = blobWithMime(res.data, name);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (error) {
      if (previewRequest.current !== requestId) return;
      setPreviewError(
        getApiErrorMessage(error, 'Не удалось открыть документ')
      );
    }
    if (previewRequest.current === requestId) {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (doc: AircraftDocument) => {
    try {
      await downloadAircraftDocument(
        doc.id,
        doc.original_name || doc.filename
      );
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось скачать документ'));
    }
  };

  const handleOpenDoc = (type: AircraftDocumentType) => {
    const files = documents.filter((item) => item.doc_type === type);
    if (files.length === 0) return;
    setPreviewFiles(files);
    void handlePreview(files[0]);
  };

  const previewIndex = previewDoc
    ? previewFiles.findIndex((item) => item.id === previewDoc.id)
    : -1;

  const goPreview = (index: number) => {
    const doc = previewFiles[index];
    if (!doc) return;
    void handlePreview(doc);
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const fleetSelect =
    aircraftList.length > 0 ? (
      <div className="aircraft-fleet-bar">
        <select
          id="aircraft-fleet-select"
          className="aircraft-fleet-select"
          value={aircraftId || ''}
          onChange={handleFleetChange}
          onFocus={() => refreshList()}
          aria-label="Выбор самолёта"
        >
          {aircraftList.map((item) => (
            <option key={item.id} value={item.id}>
              {aircraftLabel(item)}
            </option>
          ))}
        </select>
      </div>
    ) : null;

  const slg = aircraft?.slg || emptyCheck();
  const insurance = aircraft?.insurance || emptyCheck();
  const registration = aircraft?.registration || emptyCheck();
  const nearestTo = useMemo(() => pickNearestTo(to50, to100), [to50, to100]);
  const toOverdue = remainingOverdue(nearestTo);
  const hoursLabel = formatHoursPart(nearestTo);
  const toValue = hoursLabel ?? '—';
  const hourCostValue =
    hourCostAll == null || !Number.isFinite(hourCostAll)
      ? '—'
      : formatMetric(hourCostAll, 0);
  const sharePeriods = aircraft?.ownership_shares || [];
  const currentShares = currentSharePeriod(sharePeriods);
  const ownersCount = visibleOwnershipShares(currentShares).length;
  const psDocsCount = documents.filter((item) => item.doc_type === 'ps').length;
  const otherDocsCount = documents.filter(
    (item) => item.doc_type === 'other'
  ).length;

  const renderDocRow = (
    type: AircraftDocumentType,
    label: string,
    value: string,
    warn?: boolean
  ) => {
    const hasFile = documents.some((item) => item.doc_type === type);
    if (!hasFile) {
      return (
        <div className="aircraft-data-row">
          <dt>{label}</dt>
          <dd className={warn ? 'is-warn' : undefined}>{value}</dd>
        </div>
      );
    }
    return (
      <div className="aircraft-data-row is-openable has-file">
        <button
          type="button"
          className="aircraft-data-row-open"
          onClick={() => handleOpenDoc(type)}
          aria-label={`Открыть ${label}`}
        >
          <span className="aircraft-data-dt">{label}</span>
          <span className={`aircraft-data-dd${warn ? ' is-warn' : ''}`}>
            {value}
          </span>
        </button>
      </div>
    );
  };

  return (
    <div className="aircraft-page aircraft-hub">
      {feedbackNode}
      <div className="aircraft-header">
        <PageBackLink to="/" />
        <h2>Самолёт</h2>
        {fleetSelect}
      </div>

      {!aircraft ? (
        <div className="aircraft-card">
          <p className="aircraft-empty">Нет доступных самолётов в клубе</p>
        </div>
      ) : (
        <>
          <section className="aircraft-data" aria-labelledby="aircraft-info-title">
            <div className="aircraft-data-head">
              <h3 id="aircraft-info-title">Общая информация</h3>
            </div>
            <dl className="aircraft-data-list">
              <div className="aircraft-data-row">
                <dt>Модель</dt>
                <dd>{aircraft.model || '—'}</dd>
              </div>
              <div className="aircraft-data-row">
                <dt>Рег номер</dt>
                <dd>{aircraft.tail_number || '—'}</dd>
              </div>
              <div className="aircraft-data-row">
                <dt>Дата приобретения</dt>
                <dd>
                  {aircraft.purchase_date
                    ? DateFormat(aircraft.purchase_date, dateFormat)
                    : 'Не указана'}
                </dd>
              </div>
              {currentShares ? (
                <div className="aircraft-data-row is-openable has-file">
                  <button
                    type="button"
                    className="aircraft-data-row-open"
                    onClick={() => setSharesOpen(true)}
                    aria-label="Открыть историю долей"
                  >
                    <span className="aircraft-data-dt">Доли</span>
                    <span className="aircraft-data-dd">
                      {formatSharesLine(currentShares)}
                    </span>
                  </button>
                </div>
              ) : (
                <div className="aircraft-data-row">
                  <dt>Доли</dt>
                  <dd>Не указаны</dd>
                </div>
              )}
              <div className="aircraft-data-row">
                <dt>Место базировки</dt>
                <dd>{aircraft.base_airfield || '—'}</dd>
              </div>
              <div className="aircraft-data-row">
                <dt>Базовая организация/техник</dt>
                <dd>{maintenanceLabel(aircraft)}</dd>
              </div>
            </dl>
          </section>

          <section className="aircraft-data" aria-labelledby="aircraft-docs-title">
            <div className="aircraft-data-head">
              <h3 id="aircraft-docs-title">Документы</h3>
              <Link to="/aircraft/documents" className="aircraft-data-edit">
                {canMaintain ? 'Изменить' : 'Открыть'}
              </Link>
            </div>
            <dl className="aircraft-data-list">
              {renderDocRow(
                'slg',
                'СЛГ',
                checkLine(slg, dateFormat),
                slg.overdue
              )}
              {renderDocRow(
                'insurance',
                'Страховка',
                checkLine(insurance, dateFormat),
                insurance.overdue
              )}
              {renderDocRow(
                'registration',
                'Свидетельство о регистрации',
                checkLine(registration, dateFormat)
              )}
              {renderDocRow(
                'ps',
                'Свидетельство о ПС',
                psFilesLine(psDocsCount, ownersCount),
                ownersCount > 0 && psDocsCount !== ownersCount
              )}
              {renderDocRow(
                'other',
                'Иное',
                otherDocsCount ? filesCountLabel(otherDocsCount) : 'Нет'
              )}
            </dl>
          </section>

          <div
            className={
              isGuest
                ? 'aircraft-hub-grid'
                : 'aircraft-hub-grid aircraft-hub-grid-three'
            }
          >
            <HubCard
              to="/stats"
              label="Общий налёт"
              value={formatMetric(totalHours)}
              unit="м/ч"
              footer="Статистика"
            />
            <HubCard
              to="/condition"
              label="Моточасов до ближайшей формы"
              value={toValue}
              unit="м/ч"
              footer="Тех состояние"
              warn={toOverdue}
            />
            {!isGuest ? (
              <HubCard
                to="/finance"
                label="Стоимость летного часа"
                value={hourCostValue}
                unit="₽"
                footer="Финансы"
              />
            ) : null}
          </div>
        </>
      )}

      {sharesOpen ? (
        <ModalPortal onClose={() => setSharesOpen(false)}>
          <div
            className="modal-overlay"
            onClick={() => setSharesOpen(false)}
          >
            <div
              className="modal aircraft-shares-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="aircraft-shares-title"
            >
              <h3 id="aircraft-shares-title">История долей</h3>
              {sharePeriods.length === 0 ? (
                <p className="aircraft-shares-empty">Снимков пока нет</p>
              ) : (
                <ul className="aircraft-shares-list">
                  {[...sharePeriods].reverse().map((period) => (
                    <li key={period.effective_from}>
                      <span className="aircraft-shares-range">
                        {formatShareRange(period, dateFormat)}
                      </span>
                      <span className="aircraft-shares-people">
                        {formatSharesLine(period)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setSharesOpen(false)}
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      ) : null}

      {previewDoc ? (
        <DocumentPreviewModal
          title={previewDoc.original_name || previewDoc.filename}
          objectUrl={previewUrl}
          kind={previewKind}
          loading={previewLoading}
          error={previewError}
          fileIndex={Math.max(0, previewIndex)}
          fileCount={previewFiles.length}
          onPrev={() => goPreview(previewIndex - 1)}
          onNext={() => goPreview(previewIndex + 1)}
          onDownload={() => void handleDownload(previewDoc)}
          onClose={closePreview}
        />
      ) : null}
    </div>
  );
};

export default AircraftPage;
